import { memo, useState, useRef, useCallback, useEffect, Children, cloneElement, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { TransitionGroup, CSSTransition } from 'react-transition-group';
import { useClickAway } from 'react-use';
import { v4 } from 'uuid';
import { IconCrossMedium } from '@izettle/otto';
import { useIsMobile } from '../../../hooks/use-is-mobile.js';
import { focusOnTheFirstElement } from '../../../utils/focus-on-the-first-element.js';
import { StyledDividerHeader, ModalBaseBackdrop, ModalBaseOverflowingContainer, ModalBaseContainer, StyledIconButton, ModalBaseHeader, ModalBaseHeaderRow, ModalBaseLabel, ModalBaseContent, ModalBaseEndFocus } from './styles.js';
import { jsx, jsxs } from 'react/jsx-runtime';
import { ModalFooter } from '../modal-footer/components.js';
import { KeyCodes } from '../../../enumerations/key-codes.js';

const ModalBase = /*#__PURE__*/memo(_ref => {
  let {
    children,
    className,
    id = v4(),
    isVisible,
    isOverflowing,
    isOutsideClickable,
    isMobileOverride,
    preventHorizontalOverflow,
    style,
    textCloseModal,
    textLabel,
    size,
    onClose,
    onCloseEnd,
    hideTextLabel
  } = _ref;
  const [isPreAutoFocusLifecycle, setIsPreAutoFocusLifecycle] = useState(true);
  const [scrollTrigger, updateScrollTrigger] = useState({});
  const isMobile = useIsMobile();
  const isMobileOverridden = isMobileOverride != null ? isMobileOverride : isMobile;
  const closeButtonRef = useRef(null);
  const openingElementRef = useRef(null);
  const modalRef = useRef(null);
  const refContent = useRef(null);
  const modalLabelId = `modal-label-${id}`;

  /**
   * Handlers
   */

  const closeIfVisible = useCallback(() => {
    isVisible && onClose();
  }, [isVisible, onClose]);

  // Close when clicking outside modal
  useClickAway(modalRef, () => {
    if (!isOutsideClickable) {
      closeIfVisible();
    }
  });
  const handleKeyboardInput = useCallback(event => {
    if (event.keyCode === KeyCodes.Escape) {
      event.preventDefault();
      closeIfVisible();
    }
    if (event.shiftKey && event.keyCode === KeyCodes.Tab) {
      if (closeButtonRef.current === document.activeElement) {
        event.preventDefault();
      }
    }
  }, [isVisible, closeIfVisible]);
  const handleEndFocus = useCallback(() => {
    closeButtonRef.current?.focus();
  }, [closeButtonRef.current]);
  const handleTransitionExit = () => {
    if (openingElementRef?.current != null) {
      openingElementRef.current.focus();
    } else {
      focusOnTheFirstElement();
    }
    setIsPreAutoFocusLifecycle(true);
    onCloseEnd?.();
  };

  /**
   * Effects
   */

  useEffect(() => {
    if (isVisible) {
      setIsPreAutoFocusLifecycle(false);
    }
    window.addEventListener("keydown", handleKeyboardInput);
    return () => {
      window.removeEventListener("keydown", handleKeyboardInput);
    };
  }, [isVisible, handleKeyboardInput]);
  useEffect(() => {
    openingElementRef.current = document.activeElement;
  }, [isVisible]);

  /**
   * Rendering
   */

  const sortedChildren = Children.toArray(children).reduce((accumulator, child) => {
    if (!child) {
      return accumulator;
    }
    if (typeof child !== "object") {
      return {
        ...accumulator,
        content: [...accumulator.content, child]
      };
    }
    switch (child.type) {
      case ModalFooter:
      case ModalFooter:
        return {
          ...accumulator,
          footer: /*#__PURE__*/cloneElement(child, {
            isMobile,
            size
          })
        };
      default:
        return {
          ...accumulator,
          content: [...accumulator.content, child]
        };
    }
  }, {
    content: [],
    footer: null
  });
  const getContentRef = useCallback(ref => {
    if (refContent.current !== ref) {
      refContent.current = ref;
      updateScrollTrigger({});
    }
  }, [children]);
  const renderTopDivider = useMemo(() => {
    if (refContent.current && refContent.current.scrollTop > 0) {
      return /*#__PURE__*/jsx(StyledDividerHeader, {});
    }
  }, [scrollTrigger, refContent.current]);
  const renderBottomDivider = useMemo(() => {
    if (!refContent.current) return null;
    const totalScrollableHeight = refContent.current.scrollHeight;
    const visibleHeight = refContent.current.clientHeight;
    const isContentOverflowing = visibleHeight < totalScrollableHeight;
    const scrolledDistance = refContent.current.scrollTop + visibleHeight;
    const hasntReachedEnd = scrolledDistance !== totalScrollableHeight;
    if (isContentOverflowing && hasntReachedEnd) {
      return /*#__PURE__*/jsx(StyledDividerHeader, {});
    }
    return null;
  }, [scrollTrigger, refContent.current]);
  const renderContent = () => {
    return /*#__PURE__*/jsxs(ModalBaseContainer, {
      ref: modalRef,
      size: size,
      isMobile: isMobileOverridden,
      isOverflowing: isOverflowing,
      hasFooter: Boolean(sortedChildren.footer),
      className: className,
      style: style,
      children: [/*#__PURE__*/jsx(StyledIconButton, {
        variant: "minor",
        autoFocus: true,
        isMobile: isMobileOverridden,
        ref: closeButtonRef,
        svgIcon: IconCrossMedium,
        onClick: closeIfVisible,
        title: textCloseModal
      }), /*#__PURE__*/jsx(ModalBaseHeader, {
        hideTextLabel: hideTextLabel,
        children: /*#__PURE__*/jsx(ModalBaseHeaderRow, {
          isMobile: isMobileOverridden,
          children: /*#__PURE__*/jsx(ModalBaseLabel, {
            id: modalLabelId,
            children: textLabel
          })
        })
      }), renderTopDivider, /*#__PURE__*/jsx(ModalBaseContent, {
        isMobile: isMobileOverridden,
        preventHorizontalOverflow: preventHorizontalOverflow,
        hideTextLabel: hideTextLabel,
        ref: getContentRef,
        onScroll: () => updateScrollTrigger({}),
        children: sortedChildren.content
      }), renderBottomDivider, sortedChildren.footer, /*#__PURE__*/jsx(ModalBaseEndFocus, {
        tabIndex: 0,
        onFocus: handleEndFocus
      })]
    });
  };
  return /*#__PURE__*/ReactDOM.createPortal( /*#__PURE__*/jsx(TransitionGroup, {
    children: isVisible && !isPreAutoFocusLifecycle ? /*#__PURE__*/jsx(CSSTransition, {
      timeout: 300,
      classNames: "transition",
      onExit: handleTransitionExit,
      children: /*#__PURE__*/jsx(ModalBaseBackdrop, {
        isOverflowing: isOverflowing,
        isMobile: isMobileOverridden,
        role: "dialog",
        "aria-modal": true,
        "aria-hidden": false,
        "aria-labelledby": modalLabelId,
        children: isOverflowing ? /*#__PURE__*/jsx(ModalBaseOverflowingContainer, {
          children: renderContent()
        }) : renderContent()
      })
    }) : null
  }), document.body);
}, (prevProps, nextProps) => {
  return !prevProps.isVisible && !nextProps.isVisible;
});

export { ModalBase };
//# sourceMappingURL=components.js.map
